import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';

const STATE_LABEL = {
  '1a': 'Frio — sem conversa (com telefone)', '1b': 'Frio — sem telefone (usar email)',
  '2': 'Conversa incompleta', '3': 'Qualificado, sem reunião',
  '4': 'No-show (reunião perdida)', '5': 'Reunião futura/em dia', '6': 'Descartado',
};

const TEMPLATE_LABEL = {
  iniciacao: 'Iniciar conversa (SDR)', retomar: 'Retomar conversa',
  agendar: 'Empurrar agendamento', noshow: 'Reativar pós no-show',
};

export default function WhatsappCockpit({ entityPath = 'opportunities', entityId, opportunityId }) {
  const id = entityId || opportunityId;
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery(
    ['whatsapp-state', entityPath, id],
    async () => (await api.get(`/${entityPath}/${id}/whatsapp-state`)).data.data,
    { staleTime: 60000, enabled: !!id },
  );

  if (isLoading) return <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5"><div className="h-5 bg-gray-100 animate-pulse rounded w-2/3" /></section>;
  if (isError) return <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5"><p className="text-sm text-red-600">Não foi possível carregar o estado de reativação.</p></section>;
  if (!data) return null;

  const key = data.recommendedTemplateKey;
  const canSend = data.canWhatsapp && key && data.templateApproved;

  const send = async () => {
    try {
      setSending(true);
      const res = await api.post(`/${entityPath}/${id}/whatsapp/send`, { templateKey: key });
      if (res.data.success) {
        toast.success('HSM enviado');
        refetch();
        queryClient.invalidateQueries(['opp-timeline', id]);
      } else { toast.error(res.data.error || 'Falha ao enviar'); }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Falha ao enviar');
    } finally { setSending(false); setConfirm(false); }
  };

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <DevicePhoneMobileIcon aria-hidden="true" className="h-5 w-5 text-green-600" /> Reativação WhatsApp
      </h2>
      <p className="text-xs text-gray-500 mb-2">Estado: <span className="font-medium text-gray-700">{STATE_LABEL[data.state] || data.state}</span></p>

      {data.channel === 'email' && (
        <p className="text-sm text-amber-700 bg-amber-50 rounded p-2">Sem telefone — enviar email com link manualmente.</p>
      )}
      {data.channel === 'none' && (
        <p className="text-sm text-gray-500">Sem ação de reativação para este estado.</p>
      )}

      {key && (
        <>
          <p className="text-sm font-medium text-gray-800 mb-1">{TEMPLATE_LABEL[key] || key}</p>
          <div className="text-sm text-gray-600 bg-gray-50 rounded p-3 whitespace-pre-wrap mb-3">{data.preview}</div>
          {!data.templateApproved && (
            <p className="text-xs text-amber-700 mb-2">Template ainda não aprovado pela Meta — envio desabilitado.</p>
          )}
          {!confirm ? (
            <button type="button" onClick={() => setConfirm(true)} disabled={!canSend}
              className="w-full bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 text-sm">
              Enviar HSM
            </button>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={send} disabled={sending}
                className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 text-sm">
                {sending ? 'Enviando…' : 'Confirmar envio'}
              </button>
              <button type="button" onClick={() => setConfirm(false)} disabled={sending}
                className="px-3 py-2 rounded-md border border-gray-300 text-sm">Cancelar</button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
